'use client'

import {
  useLayout,
  useOnScreen,
  useQueryState,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import SettingsIcon from '@dustin-web/microsite-components/src/icon/generated/settings'
import { constants } from '@dustin-web/microsite-config/src/constants'
import { SortBy } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { Dropdown } from '@dustin-web/microsite-components/src/dropdown'
import { useEffect, useRef } from 'react'
import { ProductListListType } from '.'
import { useProductListSettings } from './use-product-list-settings'
import SearchInput from '@dustin-web/microsite-components/src/search-input'
import classNames from 'classnames'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import debounce from 'lodash/debounce'

type Props = {
  slim?: boolean
  listCategoryName?: string | null
  listCategoryId?: string
  disableSearch?: boolean
  disablePageSize?: boolean
  disableListType?: boolean
  disableMobileFilterBtn?: boolean
  onMobileFilterOverlayOpen?: () => void
}

export const ProductListOptions = ({
  slim = false,
  listCategoryName = '',
  listCategoryId,
  disableSearch = false,
  disablePageSize = false,
  disableListType = false,
  disableMobileFilterBtn = false,
  onMobileFilterOverlayOpen,
}: Props) => {
  const { translate } = useTranslations()
  const { queryState, setQueryState } = useQueryState()
  const { sortBy, pageSize } = useProductListSettings()
  const { setCookie } = useCookieState()
  const { header, responsive } = useLayout()

  const topCategoryId = '2037738'
  const isTopCategory = listCategoryId === topCategoryId
  const searchPlaceholder =
    listCategoryName && !isTopCategory
      ? `${translate('SearchIn')} '${listCategoryName}'`
      : translate('SearchText')

  const tempNoValidateAttribute = {
    noValidate: true,
  }

  const filterButtonRef = useRef<HTMLDivElement | null>(null)

  const filterButtonOnScreen = useOnScreen(
    filterButtonRef,
    `-${header.height.px.toString()}px`,
    undefined,
    true
  )

  useEffect(() => {
    if (!filterButtonOnScreen && responsive.isSmallScreen) {
      document.body.classList.add('hide-scroll-to-top-btn')
    } else {
      document.body.classList.remove('hide-scroll-to-top-btn')
    }
  }, [filterButtonOnScreen, responsive.isSmallScreen])

  const debouncedSetSearchWithin = useRef(
    debounce((value: string) => {
      setQueryState({
        page: '',
        searchwithin: value.trim(),
      })
    }, 300)
  ).current

  useEffect(() => {
    return () => debouncedSetSearchWithin.cancel()
  }, [debouncedSetSearchWithin])

  return (
    <div className="d-flex justify-content-between u-mb-8" {...tempNoValidateAttribute}>
      <div className={`d-flex product-list__sort-options product-list__fill-mobile`}>
        <div className="product-list__fill-mobile">
          <Dropdown
            buttonClassName="product-list__dropdown"
            noBorderEffect={true}
            activeItem={sortBy}
            buttonText={value => (
              <>
                {translate('SortByLead')}: {value}
              </>
            )}
            onItemClick={(value: string) => {
              DigitalData.pushSortClick(value)
              setCookie(constants.cookies.userSettings.sortBy, value.toLowerCase())
              const newState: { page?: string; sortby?: string } = { page: '' }

              // Only want to update this query parameter if it's already in the url
              if (queryState.sortby && value) {
                newState.sortby = value
              }

              setQueryState({ ...newState })
            }}
            items={[
              {
                value: SortBy.Desc.toLowerCase(),
                title: `${translate('SortType_Desc')}`,
              },
              {
                value: SortBy.Asc.toLowerCase(),
                title: `${translate('SortType_Asc')}`,
              },
              {
                value: SortBy.Sales.toLowerCase(),
                title: `${translate('SortType_Sales')}`,
              },
              {
                value: SortBy.ReviewScore.toLowerCase(),
                title: `${translate('SortType_ReviewScore')}`,
              },
            ]}
          />
        </div>
        {!disablePageSize && (
          <div className="d-none d-xl-block u-pl-4">
            <Dropdown
              noBorderEffect={true}
              activeItem={pageSize ? pageSize.toString() : ''}
              buttonText={value => (
                <>
                  {translate('ProductsPerPageLead')}: {value}
                </>
              )}
              onItemClick={(value: string) => {
                setCookie(constants.cookies.userSettings.pageSize, value)
                setQueryState({
                  page: '',
                })
              }}
              items={[
                {
                  value: '20',
                  title: `20`,
                },
                {
                  value: '50',
                  title: `50`,
                },
                {
                  value: '100',
                  title: `100`,
                },
              ]}
            />
          </div>
        )}
        {!disableSearch && !slim && (
          <div className="d-none d-xl-block flex-grow-1 u-pl-8">
            <SearchInput
              label={searchPlaceholder}
              onChange={debouncedSetSearchWithin}
              placeholder={searchPlaceholder}
            />
          </div>
        )}
      </div>
      {!disableListType && !slim && (
        <div className="d-none d-lg-flex u-pl-8">
          <ProductListListType />
        </div>
      )}
      {!disableMobileFilterBtn && !slim && (
        <div ref={filterButtonRef} className="d-lg-none u-pl-8 filter-button--wrapper">
          <button
            type="button"
            onClick={e => {
              e.preventDefault()
              onMobileFilterOverlayOpen ? onMobileFilterOverlayOpen() : null
            }}
            data-test-id="mobile-filter-btn"
            id="mobile-filter-btn"
            className={classNames('c-btn c-btn--block c-btn--primary u-mb-0 filter-button', {
              'u-fixed a-pull-from-below u-color-black u-bg-light-purple u-box-shadow':
                !filterButtonOnScreen,
            })}
            style={{
              minWidth: 0,
              zIndex: !filterButtonOnScreen ? 10 : 0,
              right: !filterButtonOnScreen ? 24 : 'auto',
              bottom: !filterButtonOnScreen ? 24 : 'auto',
              width: !filterButtonOnScreen ? 95 : 'auto',
            }}>
            <SettingsIcon className="i-20 u-mr-8" />
            <div>{translate('MobileFilterLabel')}</div>
          </button>
        </div>
      )}
    </div>
  )
}
